import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { MetricCard } from './MetricCard';
import { useMetricCalculationsMap, getMetricCompositeKey } from '@/hooks/useMetricCalculations';
import { MetricTimeseriesData } from '@/services/MetricsService';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Metric } from '@/types/metrics';

type AggregationLevel = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';


interface MetricsLibraryProps {
  settings: {
    selectedCapability: string;
  };
  metrics: Metric[];
  timeseriesData: MetricTimeseriesData | null;
  loading: boolean;
  timeseriesError: string | null;
  aggregation: AggregationLevel;
  setAggregation: (aggregation: AggregationLevel) => void;
  showKPIMode: boolean;
  setShowKPIMode: (show: boolean) => void;
  graphData: any;
  timeRange: 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year';
  setTimeRange: (timeRange: 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year') => void;
}

// Helper function to check if a metric has values in the timeseries data
const hasMetricValues = (timeseriesData: MetricTimeseriesData | null, metricKey: string): boolean => {
  if (!timeseriesData) return false;
  
  // Check if the data has the direct metric key
  if (metricKey in timeseriesData) {
    const metricData = timeseriesData[metricKey as keyof typeof timeseriesData];
    
    // If it's an array of timestamp-value pairs, check if it has any non-zero values
    if (Array.isArray(metricData)) {
      return metricData.some((item: [number, number]) => item[1] !== 0);
    }
    
    // If it's a nested object (like revenue_by_source)
    if (typeof metricData === 'object' && !Array.isArray(metricData)) {
      // Check if any of the sub-arrays have data
      return Object.keys(metricData).some(subKey => {
        const subData = (metricData as Record<string, [number, number][]>)[subKey];
        return Array.isArray(subData) && 
               subData.some((item: [number, number]) => item[1] !== 0);
      });
    }
  }
  
  return false;
};

export function MetricsLibrary({ 
  metrics,
  timeseriesData,
  loading,
  timeseriesError,
  aggregation,
  setAggregation,
  showKPIMode,
  setShowKPIMode,
  graphData,
  timeRange,
  setTimeRange
}: MetricsLibraryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for stages
  const [stages, setStages] = useState<{ id: string; label: string }[]>([]);

  // Fetch value stream stages
  useEffect(() => {
    if (!graphData) {
      console.debug('No graph data available for fetchStages');
      return;
    }

    // Check if stages exist directly, otherwise derive from capabilities
    if (graphData.stages && Array.isArray(graphData.stages)) {
      console.debug('Using stages from graph data');
      setStages(graphData.stages.map((stage: any) => ({
        id: stage.id || stage.Name?.toLowerCase().replace(/\s+/g, '-'),
        label: stage.Name || stage.label || 'Unknown Stage'
      })));
    } else if (graphData.capabilities && Array.isArray(graphData.capabilities)) {
      console.debug('Deriving stages from capabilities');
      // Derive stages from unique stageNames in capabilities
      const uniqueStages = Array.from(new Set(
        graphData.capabilities.map((cap: any) => cap.stageName || cap.Stage)
      )).filter((stageName: any) => stageName); // Filter out empty/null values

      setStages((uniqueStages as string[]).map((stageName: string, index: number) => ({
        id: `stage-${index}`,
        label: stageName.split(':')[1]?.trim() || stageName // Remove "Stage X:" prefix if present
      })));
    } else {
      console.debug('No stages or capabilities found, using fallback');
      // Don't set error state as this is not critical - fall back to metrics-derived stages
      const fallbackStages = Array.from(new Set(metrics.map(m => m["ValueStream Stage"])));
      setStages(fallbackStages.map(stage => ({ id: stage, label: stage })));
    }
  }, [graphData, metrics]);

  // Filter metrics based on search term and selected stage (memoized for performance)
  const filteredMetrics = useMemo(() => metrics.filter(metric => {
    const matchesSearch = metric["KPI Name"].toLowerCase().includes(searchTerm.toLowerCase()) ||
                         metric["KPI Description"].toLowerCase().includes(searchTerm.toLowerCase());
    
    const hasValues = showKPIMode ? true : hasMetricValues(timeseriesData, metric.metricValueKey);
    
    return matchesSearch && hasValues;
  }), [metrics, searchTerm, showKPIMode, timeseriesData]);

  // Map metrics to the descriptor shape expected by the hook
  const metricDescriptors = useMemo(() => filteredMetrics.map(m => ({
    metricValueKey: m.metricValueKey,
    operation: m.Operation,
    metricOperation: m.metricOperation,
    target: m.target,
    decreasingTarget: m.decreasingTarget,
    alertingThresholdPercentage: m.alertingThresholdPercentage,
    Id: m.Id,
  })), [filteredMetrics]);

  // Compute calculations for all filtered metrics in one pass
  const calculationsMap = useMetricCalculationsMap(
    metricDescriptors,
    timeseriesData,
    timeRange,
    showKPIMode,
  );

  return (
    <div className="space-y-4 relative">
      {timeseriesError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{timeseriesError}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between px-4 py-6">
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search metrics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[300px]"
          />
        </div>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <div className="space-y-4">
          <div className="px-4">
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowKPIMode(!showKPIMode)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    showKPIMode 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  Summary KPIs
                </button>
              </div>

              <div className="flex gap-1">
                {showKPIMode ? (
                  <div className="flex gap-1">
                    {(
                      [
                        { id: 'week', label: 'Week' },
                        { id: 'month', label: 'Month' },
                        { id: 'threeMonths', label: '3 Months' },
                        { id: 'sixMonths', label: '6 Months' },
                        { id: 'year', label: 'Year' }
                      ] as const
                    ).map(({id, label}) => (
                      <button
                        key={id}
                        onClick={() => setTimeRange(id)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          timeRange === id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-1">
                    {(['daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as AggregationLevel[]).map((level) => (
                      <button
                        key={level}
                        onClick={() => setAggregation(level)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          aggregation === level 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {stages.map(stage => (
          <TabsContent key={stage.id} value={stage.id}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMetrics
                .filter(m => m["ValueStream Stage"] === stage.label)
                .map((metric, index) => {
                  const compositeKey = getMetricCompositeKey(metric.metricValueKey, metric.Id);
                  return (
                    <MetricCard
                      key={`${stage.id}-${index}`}
                      metric={metric}
                      calculations={calculationsMap.get(compositeKey)!}
                      timeRange={timeRange}
                      isLoading={loading}
                      showKPIMode={showKPIMode}
                    />
                  );
                })}
            </div>
          </TabsContent>
        ))}
        <TabsContent value="all">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMetrics.map((metric, index) => {
              const compositeKey = getMetricCompositeKey(metric.metricValueKey, metric.Id);
              return (
                <MetricCard
                  key={`all-${index}`}
                  metric={metric}
                  calculations={calculationsMap.get(compositeKey)!}
                  timeRange={timeRange}
                  isLoading={loading}
                  showKPIMode={showKPIMode}
                />
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex flex-col items-center space-y-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-muted-foreground">Loading metrics data...</p>
          </div>
        </div>
      )}
    </div>
  );
} 
